import { ArticlesView } from './doc.article'
import { DocsTagsView } from './docs.tags'
import type { ComponentPublicInstance } from 'vue'
import { emitter } from '@/services/mitt'
import { nextTick } from 'vue'
import { platform } from '@/services/platform'
import { PodcastsView } from './docs.list.podcasts'
import { BooksView } from './doc.book'
import { PodcastEpisodesView } from './docs.list.podcasts.episodes'
import { ListView } from './docs.list'
import { Mixpanel } from '@/analytics/mixpanel'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'
import type { DocsUserDataResponse, KeyExists, WaitUI } from './interfaces'
import { router } from '@/helpers/router'
import { v4 as uuidv4 } from 'uuid'
import type {
  SearchResultGroup,
  SearchTrigger,
} from '@/analytics/mixpanel/mixpanel'
import type { ListItem } from './interfaces'

export const SEARCH_DEBOUNCE_MS = 350

/**
 * Search view.
 *
 * Responsible for interaction between the Search Vue component and the
 * rest of models: books, articles, tags to filter them.
 */
export class SearchView {
  private _vm: {
    wait: WaitUI
    searchInput: ComponentPublicInstance | null
  }

  public booksView: BooksView | null = null
  public articlesView: ArticlesView | null = null
  public tagsView: DocsTagsView | null = null
  public episodesView: PodcastEpisodesView | null = null
  public podcastsView: PodcastsView | null = null

  public search: string = ''
  public prompt: string = ''
  public searchHandler = (): void => {}
  public clearEventListeners = (): void => {}

  public hasFocus: boolean = true
  public showHeader: boolean = true
  public showTags: boolean = true
  public isSearching: boolean = false
  private isIOS: boolean = false
  private isScrolledDown: boolean = false

  private _abortController: AbortController | null = null
  private _docsUserData: DocsUserDataResponse
  private _tags: string[] = []
  private _tagsLoaded: boolean = false
  private _tagsPromise: Promise<string[]> | null = null
  private _searchId: string | null = null
  private _searchResultsIdsByDocId = new Map<string, string>()
  private _searchTimer: ReturnType<typeof setTimeout> | null = null
  private _searchGeneration: number = 0
  private _viewGenerations: Partial<
    Record<SearchResultGroup['source_widget'], number>
  > = {}

  constructor(
    vm: {
      wait: WaitUI
      searchInput: ComponentPublicInstance | null
    },
    searchTerm: string = '',
    docsUserData: DocsUserDataResponse,
  ) {
    this._vm = vm
    this.search = searchTerm
    this._docsUserData = docsUserData
  }

  /**
   * Initialize search view.
   */
  async init(): Promise<void> {
    if (this.search.trim()) {
      void this._runSearch('page_load')
    } else {
      void this._updateDocViews(this._searchGeneration, this.search)
    }

    this.searchHandler = this.scheduleSearch.bind(this)
    this.isIOS = await platform.isNativeIOSApp()
    this.showTags = this.search === ''
  }

  public scheduleSearch(): void {
    if (this._searchTimer) {
      clearTimeout(this._searchTimer)
    }
    this._searchTimer = setTimeout(() => {
      this._searchTimer = null
      void this._runSearch('debounce')
    }, SEARCH_DEBOUNCE_MS)
  }

  public submitSearch(): void {
    if (this._searchTimer) {
      clearTimeout(this._searchTimer)
      this._searchTimer = null
    }
    void this._runSearch('submit')
  }

  public dispose(): void {
    // Invalidate pending requests so they cannot update a route that the user
    // navigated to or emit results after Search has been destroyed.
    this._searchGeneration++
    if (this._searchTimer) {
      clearTimeout(this._searchTimer)
      this._searchTimer = null
    }
    this.clearEventListeners()
  }

  /**
   * Update the doc views.
   */
  private async _updateDocViews(
    generation: number,
    query: string,
  ): Promise<boolean> {
    const updateFunctions = [
      (): Promise<void> => this._updateArticlesView(generation, query),
      (): Promise<void> => this._updateBooksView(generation, query),
      (): Promise<void> => this._updatePodcastEpisodesView(generation, query),
      (): Promise<void> => this._updatePodcastsView(generation, query),
    ]

    // Run all functions asynchronously and store the promises in an array.
    const promises = updateFunctions.map((updateFunction) => updateFunction())
    const responses = await Promise.allSettled(promises)

    if (generation !== this._searchGeneration) {
      return false
    }

    // Count failed requests.
    let failedViews = 0
    responses.forEach((response) => {
      if (response.status === 'rejected') {
        // If the response failed because it was canceled,
        // it was intentional. Don't capture the error.
        if (response.reason.code === 'ERR_CANCELED') {
          return
        }

        Sentry.captureException(response.reason)
        failedViews++
      }
    })

    // If all requests failed, display an error.
    if (failedViews >= updateFunctions.length) {
      this._vm.wait.start('Loading Search')
      this._vm.wait.start('Error Loading Search')
    }

    return failedViews < updateFunctions.length
  }

  public initEventListeners(): void {
    const onAppScroll = this.onAppScroll.bind(this)
    const onFocusOut = this.focusOutHandler.bind(this)

    emitter.on('appScroll', onAppScroll)
    this._vm.searchInput?.$el.addEventListener('focusout', onFocusOut)

    this.clearEventListeners = (): void => {
      emitter.off('appScroll', onAppScroll)
      this._vm.searchInput?.$el.removeEventListener('focusout', onFocusOut)
      if (this._searchTimer) {
        clearTimeout(this._searchTimer)
        this._searchTimer = null
      }
    }
  }

  public focusOutHandler(event: FocusEvent): void {
    const searchValue = (event?.target as any).value
    // Send Page View event on un-focusing the search input.
    if (searchValue && searchValue !== '') {
      Mixpanel.trackSearchUsage()
      Mixpanel.pageView()
    }
  }

  /**
   * Search handler callback.
   *
   * Filters books, articles and tags views when the search string is updated.
   */
  private async _runSearch(trigger: SearchTrigger): Promise<void> {
    const query = this.search
    if (!query.trim()) {
      const generation = ++this._searchGeneration
      this._searchId = null
      this._clearResultViews()
      this.isSearching = true
      this.showTags = true
      await this._updateDocViews(generation, query)
      if (generation === this._searchGeneration) {
        this.isSearching = false
        this.updateQueryString('')
      }
      return
    }

    const generation = ++this._searchGeneration
    const searchId = uuidv4()
    this._searchId = searchId
    this._clearResultViews()
    this.isSearching = true
    this.showTags = false
    Mixpanel.trackSearch(searchId, query, trigger)

    const hasDisplayedResults = await this._updateDocViews(generation, query)
    if (generation !== this._searchGeneration) {
      return
    }

    this.isSearching = false
    if (!hasDisplayedResults) {
      return
    }
    this.updateQueryString(query)
    await nextTick()
    if (generation !== this._searchGeneration) {
      return
    }

    const groups = this._resultGroups()
    if (groups.every((group) => group.results.length === 0)) {
      Mixpanel.trackSearchResults(uuidv4(), searchId, 'main', groups)
    }
  }

  private _resultGroups(): SearchResultGroup[] {
    return [
      this._resultGroup(
        'books',
        this._viewGenerations.books === this._searchGeneration
          ? this.booksView?.filteredDocs.slice(0, 4)
          : [],
      ),
      this._resultGroup(
        'articles',
        this._viewGenerations.articles === this._searchGeneration
          ? this.articlesView?.filteredDocs.slice(0, 3)
          : [],
      ),
      this._resultGroup(
        'podcasts',
        this._viewGenerations.podcasts === this._searchGeneration
          ? this.podcastsView?.filteredDocs.slice(0, 4)
          : [],
      ),
      this._resultGroup(
        'podcast_episodes',
        this._viewGenerations.podcast_episodes === this._searchGeneration
          ? this.episodesView?.filteredDocs.slice(0, 3)
          : [],
      ),
    ]
  }

  private _clearResultViews(): void {
    this.booksView = null
    this.articlesView = null
    this.episodesView = null
    this.podcastsView = null
    this._searchResultsIdsByDocId.clear()
  }

  private _trackResultGroup(
    generation: number,
    sourceWidget: SearchResultGroup['source_widget'],
    docs: ListItem[],
  ): void {
    if (
      generation !== this._searchGeneration ||
      !this._searchId ||
      docs.length === 0
    ) {
      return
    }

    const searchResultsId = uuidv4()
    docs.forEach((doc) => {
      this._searchResultsIdsByDocId.set(doc.id, searchResultsId)
    })
    Mixpanel.trackSearchResults(searchResultsId, this._searchId, 'main', [
      this._resultGroup(sourceWidget, docs),
    ])
  }

  private _resultGroup(
    sourceWidget: SearchResultGroup['source_widget'],
    docs: ListItem[] = [],
  ): SearchResultGroup {
    return {
      source_widget: sourceWidget,
      rank_offset: 0,
      results: docs.map((doc) => doc.url_slug),
    }
  }

  public docViewEventProperties(doc: ListItem): Record<string, unknown> {
    const searchResultsId = this._searchResultsIdsByDocId.get(doc.id)
    return searchResultsId ? { search_results_id: searchResultsId } : {}
  }

  /**
   * Update query string with the new search term.
   */
  updateQueryString(searchValue: string): void {
    router().push({ query: { term: searchValue } })
  }

  /**
   * Create a new instance of `ArticlesView` or filter it with
   * the current search term if it already exists.
   */
  private async _updateArticlesView(
    generation: number,
    query: string,
  ): Promise<void> {
    this._vm.wait.start('Articles')

    try {
      const view = await ArticlesView.createSearchView(
        query,
        this._docsUserData,
      )
      if (generation === this._searchGeneration) {
        this.articlesView = view
        this._viewGenerations.articles = generation
        this._trackResultGroup(
          generation,
          'articles',
          view.filteredDocs.slice(0, 3),
        )
      }
    } catch (error: any) {
      // Rethrow the error so we can catch it in `this._updateDocViews()`.
      throw error
    } finally {
      if (generation === this._searchGeneration) {
        this._vm.wait.end('Articles')
      }
    }
  }

  /**
   * Create a new instance of `DocsTagsView`
   */
  private async _setupTagsView(): Promise<string[]> {
    if (this._tagsPromise) {
      return this._tagsPromise
    }

    this._vm.wait.start('Tags')
    this._tagsPromise = backend.getBookTags().then((tags) => {
      this._tags = tags
      this._tagsLoaded = true
      this.tagsView = new DocsTagsView(tags, 'books', 'Book Categories')
      return tags
    })

    try {
      return await this._tagsPromise
    } catch (error: any) {
      this._tagsPromise = null
      // Rethrow the error so we can catch it in `this._updateDocViews()`.
      throw error
    } finally {
      this._vm.wait.end('Tags')
    }
  }

  /**
   * Create a new instance of `BooksView` or filter it with
   * the current search term if it already exists.
   */
  private async _updateBooksView(
    generation: number,
    query: string,
  ): Promise<void> {
    this._vm.wait.start('Books')

    try {
      if (!this._tagsLoaded) {
        await this._setupTagsView()
      }
      if (generation !== this._searchGeneration) {
        return
      }
      const view = await BooksView.createSearchView(
        this._tags,
        query,
        this._docsUserData,
      )
      if (generation === this._searchGeneration) {
        this.booksView = view
        this._viewGenerations.books = generation
        this._trackResultGroup(
          generation,
          'books',
          view.filteredDocs.slice(0, 4),
        )
      }
    } catch (error: any) {
      // Rethrow the error so we can catch it in `this._updateDocViews()`.
      throw error
    } finally {
      if (generation === this._searchGeneration) {
        this._vm.wait.end('Books')
      }
    }
  }

  /**
   * Create a new instance of `PodcastsSearchView` or filter it with
   * the current search term if it already exists.
   */
  private async _updatePodcastsView(
    generation: number,
    query: string,
  ): Promise<void> {
    this._vm.wait.start('Podcasts')

    try {
      if (query) {
        const view = await PodcastsView.createSearchView(
          query,
          this._docsUserData,
        )
        if (generation === this._searchGeneration) {
          this.podcastsView = view
          this._viewGenerations.podcasts = generation
          this._trackResultGroup(
            generation,
            'podcasts',
            view.filteredDocs.slice(0, 4),
          )
        }
      } else {
        // Note regarding the abort controller:
        // it was present in the now removed search.podcasts.ts file.
        // I am not sure if we need it. The logic was like this:
        // - if there is a search term, we use the search view
        // - if there is no search term, we load all podcasts and use the
        // abort controller to cancel the previous request.
        // But in which case this is possible? The "no search term" case is
        // only relevant when we did not enter anything in the search field,
        // so how we can do this twice (so we have the previous request to
        // cancel)?
        // https://github.com/shortformhq/main/commit/671cd99e0295c048e316b2eb6216204d37ad15fb

        // Abort the previous request before sending a new one.
        this._abortController?.abort()

        // Create a new AbortController so that we can cancel
        // this request if a new query is made before it's fulfilled.
        this._abortController = new AbortController()
        const signal = this._abortController.signal

        const response = await backend.getPodcasts(signal)
        const view = new PodcastsView(response.slice(0, 4))
        view.setUserData(this._docsUserData)
        if (generation === this._searchGeneration) {
          this.podcastsView = view
          this._viewGenerations.podcasts = generation
        }
      }
    } catch (error: any) {
      // Rethrow the error so we can catch it in `this._updateDocViews()`.
      throw error
    } finally {
      if (generation === this._searchGeneration) {
        this._vm.wait.end('Podcasts')
      }
    }
  }

  /**
   * Create a new instance of `PodcastEpisodesView` or filter it with
   * the current search term if it already exists.
   */
  private async _updatePodcastEpisodesView(
    generation: number,
    query: string,
  ): Promise<void> {
    this._vm.wait.start('Episodes')

    try {
      const view = await PodcastEpisodesView.createSearchView(
        query,
        this._docsUserData,
      )
      if (generation === this._searchGeneration) {
        this.episodesView = view
        this._viewGenerations.podcast_episodes = generation
        this._trackResultGroup(
          generation,
          'podcast_episodes',
          view.filteredDocs.slice(0, 3),
        )
      }
    } catch (error: any) {
      // Rethrow the error so we can catch it in `this._updateDocViews()`.
      throw error
    } finally {
      if (generation === this._searchGeneration) {
        this._vm.wait.end('Episodes')
      }
    }
  }

  get articlesListView(): ListView | null {
    if (!this.articlesFound()) {
      return null
    }

    const title = 'Articles'
    const viewAllLink =
      this.search && this.articlesView?.filteredDocs.length
        ? `/app/articles/search/${encodeURIComponent(this.search)}`
        : `/app/articles/list/all`

    return new ListView(title, this.articlesView, {
      viewAllLink,
      viewAllQuery: this._viewAllQuery,
    })
  }

  get booksListView(): ListView | null {
    if (!this.booksFound()) {
      return null
    }

    const title = this.search ? 'Books' : 'Popular Books'
    const viewAllLink = this.search
      ? `/app/books/search/${encodeURI(this.search)}`
      : '/app/books/list/popular'

    return new ListView(title, this.booksView, {
      viewAllLink,
      viewAllQuery: this._viewAllQuery,
    })
  }

  get episodesListView(): ListView | null {
    if (!this.episodesFound()) {
      return null
    }

    const title = 'Podcast Episodes'
    const viewAllLink = this.search
      ? `/app/podcasts/episodes/search/${this.search}`
      : '/app/podcasts'
    const viewAllText = this.search ? 'View all' : 'View all podcasts'

    return new ListView(title, this.episodesView, {
      viewAllLink,
      viewAllText,
      viewAllQuery: this._viewAllQuery,
    })
  }

  get podcastsListView(): ListView | null {
    if (!this.podcastsFound()) {
      return null
    }

    const title = this.search ? 'Podcasts' : 'Popular Podcasts'
    const viewAllLink = this.search
      ? `/app/podcasts/search/${encodeURI(this.search)}`
      : '/app/podcasts'

    return new ListView(title, this.podcastsView, {
      viewAllLink,
      viewAllQuery: this._viewAllQuery,
    })
  }

  private get _viewAllQuery(): Record<string, string> | undefined {
    return this._searchId ? { search_id: this._searchId } : undefined
  }

  /*
   * Show the empty podcasts block if books or articles have been found but no podcasts.
   */
  get showEmptyPodcastBlock(): boolean {
    return (
      (this.articlesFound() || this.booksFound()) &&
      !this.episodesFound() &&
      !this.podcastsFound()
    )
  }

  booksFound(): this is KeyExists<typeof this, 'booksView'> {
    return this.booksView !== null && this.booksView.filteredDocs.length > 0
  }

  episodesFound(): this is KeyExists<typeof this, 'episodesView'> {
    if (!this.episodesView) {
      return false
    }
    return this.episodesView.filteredDocs.length > 0
  }

  podcastsFound(): this is KeyExists<typeof this, 'podcastsView'> {
    if (!this.podcastsView) {
      return false
    }
    return this.podcastsView.podcasts.length > 0
  }

  articlesFound(): this is KeyExists<typeof this, 'articlesView'> {
    return (
      this.articlesView !== null && this.articlesView.filteredDocs.length > 0
    )
  }

  get showEmptyBlock(): boolean {
    return (
      this.search.trim() !== '' &&
      !this.isSearching &&
      !this.booksFound() &&
      !this.articlesFound() &&
      !this.podcastsFound() &&
      !this.episodesFound()
    )
  }

  hasTags(): this is KeyExists<typeof this, 'tagsView'> {
    return this.tagsView !== null && this.tagsView.tags.length > 0
  }

  toggleFocus(): void {
    this.hasFocus = !this.hasFocus
  }

  clearSearchOnEscape(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.search = ''
    }
  }

  get headerClass(): any {
    return {
      'search__header--focus': !this.hasFocus || this.search,
      'search__header--scrolled': this.isScrolledDown,
      'search__header--ios': this.isIOS,
    }
  }

  /**
   * App scroll handler.
   *
   * When user scrolls on the mobile, we unfocus the search field to
   * hide a virtual keyboard.
   */
  private onAppScroll(appElement: HTMLElement): void {
    // 50 is roughly the height of the header in the Search page.
    // This is a little buffer zone to where we define the top
    // of the page, and it doesn't need to match exactly
    // the header height.
    this.isScrolledDown = appElement.scrollTop >= 50

    if (platform.isMobileWidth()) {
      nextTick(() => {
        this._vm.searchInput?.$el.blur()
      })
    }

    // Close header User menu on scroll, otherwise it looks buggy
    emitter.emit('closeOverlayMenu')
  }
}
